import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Target, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLP = (n) => {
  if (n === null || n === undefined) return '—';
  const num = Number(n) || 0;
  return num.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

const StateBadge = ({ label }) => {
  if (!label) return <span className="text-xs text-[#6A736B]">—</span>;
  const lower = label.toLowerCase();
  let cls = 'bg-[#F3EBE1] text-[#6A736B] border-[#E8E5DD]';
  if (lower.includes('agend')) cls = 'bg-blue-50 text-blue-700 border-blue-200';
  else if (lower.includes('confirm')) cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  else if (lower.includes('pend')) cls = 'bg-amber-50 text-amber-700 border-amber-200';
  else if (lower.includes('cancel')) cls = 'bg-red-50 text-red-700 border-red-200';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${cls}`}>
      {label}
    </span>
  );
};

const PaymentLogCell = ({ log }) => {
  if (!log || log.length === 0) return <span className="text-xs text-[#6A736B] italic">Sin abonos</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {log.map((p, idx) => {
        const date = p.fecha_pago || p.fecha;
        const isReal = !!p.fecha_pago;
        return (
          <span
            key={`${date}-${idx}`}
            className="text-[11px] font-mono text-emerald-700 whitespace-nowrap"
            title={
              isReal
                ? `Fecha real de la boleta Medilink${p.source ? ` · ${p.source}` : ''}`
                : `Fecha de registro (sin boleta Medilink enlazada)${p.source ? ` · ${p.source}` : ''}`
            }
          >
            {date}: {CLP(p.monto)}
            {!isReal && <span className="text-amber-600 ml-1" aria-label="fecha estimada">*</span>}
          </span>
        );
      })}
      {log.some((p) => !p.fecha_pago) && (
        <span className="text-[10px] text-amber-600 italic">* fecha de registro — no se enlazó boleta Medilink</span>
      )}
    </div>
  );
};

export const ConsultoraProjectionDialog = ({ open, onClose, consultant, dateRange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !consultant?.consultant_id) return;
    const fetchDetail = async () => {
      setLoading(true);
      setData(null);
      try {
        const params = new URLSearchParams();
        if (dateRange?.start) params.set('start_date', dateRange.start);
        if (dateRange?.end) params.set('end_date', dateRange.end);
        const { data: res } = await axios.get(
          `${API_URL}/api/management/consultora/${consultant.consultant_id}/projection?${params}`
        );
        setData(res);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchDetail();
  }, [open, consultant?.consultant_id, dateRange?.start, dateRange?.end]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-[95vw] lg:max-w-6xl xl:max-w-7xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="consultora-projection-dialog"
      >
        <DialogHeader className="border-b border-[#E8E5DD] pb-3">
          <DialogTitle className="flex items-center gap-2 text-[#1A1C18]">
            <Target size={18} className="text-[#8B5CF6]" />
            <span>Proyección — {consultant?.consultant_name || 'Consultora'}</span>
            {data && (
              <span className="text-sm font-normal text-[#6A736B]">
                · {data.count} cirugías agendadas
              </span>
            )}
          </DialogTitle>
          {dateRange?.start && dateRange?.end && (
            <p className="text-xs text-[#6A736B] mt-1">
              Período: {dateRange.start} → {dateRange.end} · Horario Chile (America/Santiago) · Datos 100% locales
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-auto -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#6A736B] gap-2">
              <Loader2 className="animate-spin" size={18} />
              <span>Cargando detalle desde base de datos local...</span>
            </div>
          ) : !data || data.count === 0 ? (
            <p className="py-12 text-center text-sm text-[#6A736B]">
              Sin cirugías agendadas pendientes de operar para esta consultora en el período.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="consultora-projection-detail-table">
              <thead className="sticky top-0 bg-[#FDFBF7] z-10">
                <tr className="text-left text-[#6A736B] border-b border-[#E8E5DD]">
                  <th className="py-2.5 px-3 font-semibold whitespace-nowrap">ID Atención</th>
                  <th className="py-2.5 px-3 font-semibold whitespace-nowrap">RUT</th>
                  <th className="py-2.5 px-3 font-semibold">Paciente</th>
                  <th className="py-2.5 px-3 font-semibold">Cirugía</th>
                  <th className="py-2.5 px-3 font-semibold">Estado</th>
                  <th className="py-2.5 px-3 font-semibold whitespace-nowrap">Fecha Reserva</th>
                  <th className="py-2.5 px-3 font-semibold whitespace-nowrap">Fecha Cirugía</th>
                  <th className="py-2.5 px-3 font-semibold text-right whitespace-nowrap">Monto Total</th>
                  <th className="py-2.5 px-3 font-semibold text-right whitespace-nowrap text-emerald-700">Monto Abonado</th>
                  <th className="py-2.5 px-3 font-semibold whitespace-nowrap">Historial de Abonos</th>
                </tr>
              </thead>
              <tbody>
                {data.surgeries.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-[#E8E5DD]/60 hover:bg-[#FDFBF7]"
                    data-testid={`consultora-detail-row-${s.id}`}
                  >
                    <td className="py-2.5 px-3 font-mono text-xs text-[#6A736B]">{s.atencion_id || '—'}</td>
                    <td className="py-2.5 px-3 font-mono text-xs text-[#1A1C18]">{s.patient_rut || '—'}</td>
                    <td className="py-2.5 px-3 font-medium text-[#1A1C18] whitespace-nowrap">{s.patient_name || '—'}</td>
                    <td className="py-2.5 px-3 text-[#1A1C18]">{s.surgery_type || '—'}</td>
                    <td className="py-2.5 px-3"><StateBadge label={s.state} /></td>
                    <td className="py-2.5 px-3 font-mono text-xs text-[#6A736B] whitespace-nowrap">{s.fecha_reserva || '—'}</td>
                    <td className="py-2.5 px-3 font-mono text-xs text-[#1A1C18] whitespace-nowrap">
                      {s.fecha_cirugia}{s.hora_cirugia ? ` · ${s.hora_cirugia}` : ''}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-right text-[#1A1C18]">{CLP(s.atencion_total)}</td>
                    <td className="py-2.5 px-3 font-mono text-right text-emerald-700 font-semibold">{CLP(s.atencion_abonado)}</td>
                    <td className="py-2.5 px-3"><PaymentLogCell log={s.payment_log} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-white">
                <tr className="border-t-2 border-[#1A1C18] bg-[#FDFBF7] font-bold">
                  <td colSpan={7} className="py-3 px-3 text-right text-[#1A1C18] uppercase text-xs tracking-wider">
                    Totales — {data.count} cirugías
                  </td>
                  <td className="py-3 px-3 font-mono text-right text-[#1A1C18]" data-testid="consultora-detail-total-facturado">
                    {CLP(data.totals.total_facturado)}
                  </td>
                  <td className="py-3 px-3 font-mono text-right text-emerald-700" data-testid="consultora-detail-total-abonado">
                    {CLP(data.totals.total_abonado)}
                  </td>
                  <td className="py-3 px-3 font-mono text-right text-red-700 text-xs" title="Brecha de Cobro">
                    Brecha: {CLP(data.totals.brecha_cobro)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
